// Capacitor bridge — thin wrapper over native plugins.
// Always safe to call in a plain browser — all methods no-op gracefully.
//
// Usage:
//   import { hapticTap, onAppStateChange } from './cap-bridge';

// ─── Capacitor detection ──────────────────────────────────────────────────────

export const isNative: boolean =
  typeof window !== 'undefined' &&
  !!(window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } })
    .Capacitor?.isNativePlatform?.();

// ─── Haptics ──────────────────────────────────────────────────────────────────
// Light tap feedback on game actions.

export async function hapticTap(): Promise<void> {
  if (!isNative) return;
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch { /* no haptics on this device */ }
}

export async function hapticSuccess(): Promise<void> {
  if (!isNative) return;
  try {
    const { Haptics, NotificationType } = await import('@capacitor/haptics');
    await Haptics.notification({ type: NotificationType.Success });
  } catch { /* no haptics */ }
}

// ─── App lifecycle ────────────────────────────────────────────────────────────
// Fires when the app goes to background / returns to foreground.
// Used by socket.ts to close the WebSocket before iOS suspends the process
// (prevents the server from hanging on a stale connection).

type AppStateCallback = (active: boolean) => void;

let removeAppStateListener: (() => void) | null = null;

export async function onAppStateChange(cb: AppStateCallback): Promise<void> {
  if (!isNative) return;

  try {
    const { App } = await import('@capacitor/app');

    const handle = await App.addListener('appStateChange', ({ isActive }) => {
      cb(isActive);
    });

    // Keep a reference so boot code can cleanly remove if needed
    removeAppStateListener = () => handle.remove();
  } catch { /* not available */ }
}

export function removeAppStateHandler(): void {
  removeAppStateListener?.();
  removeAppStateListener = null;
}

// ─── Status bar ──────────────────────────────────────────────────────────────
// Match the dark app background.

export async function initStatusBar(): Promise<void> {
  if (!isNative) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#1a1a1a' });
  } catch { /* no status bar api */ }
}
